// src/components/PrivacyPolicy.jsx

import React, { useEffect } from "react";

/* ─── CSS ─────────────────────────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,600&family=Inter:wght@400;500;600&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.jbpp {
  min-height: 100vh;
  background: linear-gradient(160deg, #1A0505 0%, #3D0A0A 50%, #5A1010 100%);
  font-family: 'Inter', sans-serif;
  position: relative;
  overflow-x: hidden;
}

/* dot-grid watermark */
.jbpp-dust {
  position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background-image: radial-gradient(circle, rgba(201,150,42,0.10) 1px, transparent 1px);
  background-size: 36px 36px;
}

.jbpp-wrap {
  position: relative; z-index: 1;
  max-width: 900px;
  margin: 0 auto;
  padding: clamp(32px,6vw,72px) clamp(16px,5vw,40px) clamp(40px,6vw,72px);
}

/* ── Hero header ── */
.jbpp-hero {
  text-align: center;
  margin-bottom: clamp(36px, 5vw, 56px);
}
.jbpp-om {
  display: block;
  font-size: clamp(36px, 6vw, 52px);
  color: #C9962A;
  font-family: serif;
  line-height: 1;
  margin-bottom: 14px;
  filter: drop-shadow(0 0 12px rgba(201,150,42,0.3));
}
.jbpp-eyebrow {
  display: inline-flex; align-items: center; gap: 10px;
  font-size: clamp(10px, 1.1vw, 11.5px);
  font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase;
  color: #C9962A; margin-bottom: 14px;
}
.jbpp-eline { width: 28px; height: 1px; background: linear-gradient(90deg, transparent, #C9962A); }
.jbpp-eline.r { background: linear-gradient(90deg, #C9962A, transparent); }

.jbpp-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(28px, 5vw, 46px);
  font-weight: 700;
  color: #FDF6EC;
  margin-bottom: 16px;
  line-height: 1.2;
}
.jbpp-title em { font-style: italic; color: #E8C46A; }

.jbpp-intro {
  font-size: clamp(13px, 1.4vw, 15px);
  color: rgba(253,246,236,0.72);
  line-height: 1.75;
  max-width: 640px;
  margin: 0 auto;
}
.jbpp-intro a { color: #C9962A; text-decoration: underline; }
.jbpp-intro strong { color: #E8C46A; font-weight: 600; }

/* gold lotus divider */
.jbpp-divrow {
  display: flex; align-items: center; justify-content: center;
  gap: 14px; margin: clamp(24px,3.5vw,36px) 0;
}
.jbpp-divline { width: clamp(48px,7vw,100px); height: 1px; background: linear-gradient(90deg, transparent, #C9962A); }
.jbpp-divline.r { background: linear-gradient(90deg, #C9962A, transparent); }

/* ── Section cards ── */
.jbpp-sections { display: flex; flex-direction: column; gap: clamp(12px,1.8vw,18px); }

.jbpp-card {
  background: rgba(201,150,42,0.06);
  border: 1px solid rgba(201,150,42,0.2);
  border-radius: 14px;
  padding: clamp(18px,3vw,28px) clamp(18px,3vw,28px);
  transition: border-color 0.2s, background 0.2s;
  cursor: default;
}
.jbpp-card:hover {
  border-color: rgba(201,150,42,0.45);
  background: rgba(201,150,42,0.09);
}

/* number badge + title row */
.jbpp-card-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: clamp(12px, 1.8vw, 16px);
}
.jbpp-num {
  width: clamp(32px,4vw,40px);
  height: clamp(32px,4vw,40px);
  border-radius: 50%;
  background: rgba(201,150,42,0.15);
  border: 1.5px solid rgba(201,150,42,0.4);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Playfair Display', serif;
  font-size: clamp(13px,1.4vw,16px);
  font-weight: 700;
  color: #C9962A;
  flex-shrink: 0;
}
.jbpp-card-title {
  font-family: 'Playfair Display', serif;
  font-size: clamp(15px, 1.7vw, 19px);
  font-weight: 700;
  color: #FDF6EC;
  line-height: 1.3;
}

/* bullet list */
.jbpp-list {
  list-style: none;
  display: flex; flex-direction: column; gap: 9px;
  padding-left: clamp(44px,5vw,54px);
}
.jbpp-list li {
  position: relative;
  font-size: clamp(12.5px, 1.3vw, 14px);
  color: rgba(253,246,236,0.72);
  line-height: 1.7;
}
.jbpp-list li::before {
  content: '';
  position: absolute;
  left: -16px; top: 9px;
  width: 5px; height: 5px;
  border-radius: 50%;
  background: #C9962A;
  opacity: 0.7;
}
.jbpp-list a { color: #C9962A; text-decoration: underline; word-break: break-word; }

/* ── Footer ── */
.jbpp-footer {
  text-align: center;
  margin-top: clamp(36px, 5vw, 52px);
  padding-top: clamp(20px, 3vw, 28px);
  border-top: 1px solid rgba(201,150,42,0.2);
}
.jbpp-footer-sym {
  font-size: 20px; color: #C9962A; opacity: 0.5; margin-bottom: 10px;
}
.jbpp-footer p {
  font-size: clamp(12px, 1.2vw, 13.5px);
  color: rgba(253,246,236,0.55);
  line-height: 1.65;
}
.jbpp-footer a { color: #C9962A; text-decoration: underline; }
.jbpp-footer strong { color: rgba(253,246,236,0.75); }

/* ── Responsive ── */
@media (max-width: 480px) {
  .jbpp-list { padding-left: 20px; }
  .jbpp-card-head { gap: 10px; }
}
`;

/* ─── Lotus SVG ──────────────────────────────────────────────────────────── */
const Lotus = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 28 28"
    fill="none"
    aria-hidden="true"
  >
    <ellipse cx="14" cy="16" rx="4" ry="9" fill="#C9962A" opacity=".9" />
    <ellipse
      cx="14"
      cy="16"
      rx="4"
      ry="8"
      fill="#C9962A"
      opacity=".85"
      transform="rotate(-30 14 16)"
    />
    <ellipse
      cx="14"
      cy="16"
      rx="4"
      ry="8"
      fill="#C9962A"
      opacity=".85"
      transform="rotate(30 14 16)"
    />
    <ellipse
      cx="14"
      cy="16"
      rx="3.5"
      ry="6"
      fill="#C9962A"
      opacity=".6"
      transform="rotate(-58 14 16)"
    />
    <ellipse
      cx="14"
      cy="16"
      rx="3.5"
      ry="6"
      fill="#C9962A"
      opacity=".6"
      transform="rotate(58 14 16)"
    />
    <ellipse cx="14" cy="24" rx="7" ry="2.5" fill="#C9962A" opacity=".25" />
  </svg>
);

/* ─── Data ───────────────────────────────────────────────────────────────── */
const sections = [
  {
    title: "Information We Collect",
    content: [
      "When you register on Jain Var Vadhu, we collect information such as your photo, profile, email, name, date of birth, educational qualifications, ID proof documents, mailing address, and phone number.",
      "We use secure servers for all transactions. Cookies are used to store login sessions and improve our services. Some third-party cookies may also be used.",
      "If you purchase paid services, we may collect billing details, card information, and payment tracking data.",
      "Depending on your usage, we may also collect device details, location data, and log files.",
      "This information is used for verification, fraud prevention, data analysis, and service improvement.",
    ],
  },
  {
    title: "How We Use Your Information",
    content: [
      "To provide, personalise, and improve your experience on our Jain matrimonial platform.",
      "For trend analysis, fraud detection, and better value-based matchmaking.",
      "To communicate service updates, auspicious announcements, and important notices.",
    ],
  },
  {
    title: "For European Union (EU) Members",
    content: [
      "EU users have the right to provide or withdraw consent for personal information processing.",
      "You may request to review, update, or delete your data by emailing us at jainVar Vadhu@gmail.com.",
      "We may transfer and store your information outside the EU with adequate safeguards in place.",
    ],
  },
  {
    title: "Sharing of Information",
    content: [
      "We may share your information with affiliates, regulators, or law enforcement where legally required.",
      "In case of mergers or acquisitions, your information may be transferred as part of business assets.",
      "We are not responsible for the privacy practices of third-party websites linked from our platform.",
    ],
  },
  {
    title: "Data Retention",
    content: [
      "We retain your data for as long as your account is active or as required for legal, regulatory, or business purposes.",
    ],
  },
  {
    title: "Security Measures",
    content: [
      "We adopt organisational, technical, and administrative measures to safeguard your data.",
      "However, no method of data transmission or storage over the Internet is 100% secure.",
    ],
  },
  {
    title: "Updates to Privacy Policy",
    content: [
      "We may revise this Privacy Policy from time to time. Updated versions will be posted on this page, and significant changes may be communicated via email or platform notifications.",
    ],
  },
];

/* ─── Component ───────────────────────────────────────────────────────────── */
const PrivacyPolicy = () => {
  useEffect(() => {
    const id = "jbpp-styles";
    if (!document.getElementById(id)) {
      const el = document.createElement("style");
      el.id = id;
      el.textContent = CSS;
      document.head.appendChild(el);
    }
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);

  const renderPoint = (point) => {
    const emailMatch = point.match(/[\w.-]+@[\w.-]+\.\w+/);
    if (emailMatch) {
      const [before, after] = point.split(emailMatch[0]);
      return (
        <>
          {before}
          <a href={`mailto:${emailMatch[0]}`}>{emailMatch[0]}</a>
          {after}
        </>
      );
    }
    return point;
  };

  return (
    <div className="jbpp">
      <div className="jbpp-dust" aria-hidden="true" />

      <div className="jbpp-wrap">
        {/* ── Hero ── */}
        <header className="jbpp-hero">
          <span className="jbpp-om" aria-label="Om">
            ꣽ
          </span>

          <div className="jbpp-eyebrow" aria-hidden="true">
            <span className="jbpp-eline" />
            Jain Var Vadhu · Privacy &amp; Trust
            <span className="jbpp-eline r" />
          </div>

          <h1 className="jbpp-title">
            Privacy <em>Policy</em>
          </h1>

          <p className="jbpp-intro">
            This Privacy Policy applies to{" "}
            <a
              href="https://www.jainVar Vadhu.com"
              target="_blank"
              rel="noreferrer"
            >
              www.jainVar Vadhu.com
            </a>
            , a sacred matrimonial platform operated by{" "}
            <strong>Jain Var Vadhu</strong>. By using our services, you agree to
            the collection, use, and sharing of your information as outlined in
            this policy — handled with the utmost Dharmic integrity and respect
            for your privacy.
          </p>
        </header>

        {/* lotus divider */}
        <div className="jbpp-divrow" aria-hidden="true">
          <div className="jbpp-divline" />
          <Lotus />
          <div className="jbpp-divline r" />
        </div>

        {/* ── Sections ── */}
        <main>
          <div className="jbpp-sections" role="list">
            {sections.map((sec, i) => (
              <article key={i} className="jbpp-card" role="listitem">
                <div className="jbpp-card-head">
                  <div className="jbpp-num" aria-hidden="true">
                    {i + 1}
                  </div>
                  <h2 className="jbpp-card-title">{sec.title}</h2>
                </div>
                <ul className="jbpp-list" aria-label={sec.title}>
                  {sec.content.map((pt, j) => (
                    <li key={j}>{renderPoint(pt)}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </main>

        {/* ── Footer ── */}
        <footer className="jbpp-footer">
          <div className="jbpp-footer-sym" aria-hidden="true">
            ☸
          </div>
          <p>
            <strong>Divine Values. Sacred Bond. Eternal Together.</strong>
          </p>
          <p style={{ marginTop: 8 }}>
            For questions or data requests, reach out at{" "}
            <a href="mailto:jainVar Vadhu@gmail.com">jainVar Vadhu@gmail.com</a>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
